import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getApiErrorMessage } from '../../../utils/errorUtils';
import ProfileCommon from './index';
import useProfileForm from '../../../hooks/features/profile/useProfileForm';
import {
  PersonalInfoSection,
  ContactInfoSection,
  PasswordSection,
  RolesSection,
} from './sections';
import useUnsavedChangesWarning from '../../../hooks/ui/navigation/useUnsavedChangesWarning';

const SharedProfile = ({
  userData,
  canEditProfile = false,
  showPasswordTab = false,
  showRolesTab = true,
  requireCurrentPassword = true,
  onSave,
  onVerifyPassword,
  onChangePassword,
  personalFields: customPersonalFields,
  contactFields: customContactFields,
  allowEmailEdit = false,
  roleId = null,
  roleFields,
  showPrivilegesEditor = false,
  roleOptions = [],
  categoryOptions = [],
  onSaveRoleAndPrivileges,
  isInternal = true,
  privilegesUserId = null,
  userPrivileges = null,
  hasCustomPrivileges = false,
}) => {
  const { t } = useTranslation();

  const {
    form,
    setForm,
    passwordVerified,
    setPasswordVerified,
    activeTab,
    editingPersonal,
    setEditingPersonal,
    editingContact,
    setEditingContact,
    discardChanges,
    discardRoleFields,
    handleTabChange,
  } = useProfileForm(userData);

  const personalFields = customPersonalFields || [
    {
      name: 'username',
      type: 'text',
      label: t('profileData.userName'),
      required: true,
      fullWidth: true,
    },
  ];
  const contactFields = customContactFields || [
    {
      name: 'email',
      type: 'email',
      label: t('Email'),
      required: true,
      fullWidth: true,
      disabled: !allowEmailEdit,
    },
    {
      name: 'phone',
      type: 'phoneWithCountry',
      label: t('profileData.phoneNumber'),
      required: false,
      fullWidth: true,
    },
  ];

  const [editingPrivileges, setEditingPrivileges] = useState(false);
  const [editingRoles, setEditingRoles] = useState(false);
  const [rolesFieldsDirty, setRolesFieldsDirty] = useState(false);
  const [mountedTabs, setMountedTabs] = useState(() => new Set([0]));
  const privilegesSaveRef = useRef(null);

  useEffect(() => {
    setMountedTabs((prev) => new Set([...prev, activeTab]));
  }, [activeTab]);

  const handlePrivilegesDirtyChange = useCallback((dirty) => {
    setEditingPrivileges(dirty);
  }, []);

  const handleRoleStartEdit = useCallback(() => {
    setEditingRoles(true);
  }, []);

  const handleRoleStopEdit = useCallback(() => {
    setEditingRoles(false);
    setRolesFieldsDirty(false);
  }, []);

  const handleRoleDiscard = useCallback(() => {
    discardRoleFields();
    setEditingRoles(false);
    setRolesFieldsDirty(false);
  }, [discardRoleFields]);

  const handleSaveProfile = useCallback(
    async (section) => {
      if (!onSave) return false;
      try {
        await onSave(form, section);
        toast.success(t('profileData.updated'));
        return true;
      } catch (e) {
        toast.error(getApiErrorMessage(e, t('profileData.updateError')));
        return false;
      }
    },
    [form, onSave, t]
  );

  const isDirty =
    editingPersonal || editingContact || editingPrivileges || rolesFieldsDirty;

  useUnsavedChangesWarning(isDirty);

  const handleVerifyPassword = useCallback(
    async (currentPassword) => {
      if (!onVerifyPassword) return;
      try {
        await onVerifyPassword(currentPassword);
        setPasswordVerified(true);
        toast.success(t('profileData.verified'));
      } catch (e) {
        setPasswordVerified(false);
        toast.error(t('profileData.verifyFailed'));
      }
    },
    [onVerifyPassword, setPasswordVerified, t]
  );

  const handleChangePassword = useCallback(
    async (currentPassword, newPassword) => {
      if (!onChangePassword) return;
      try {
        await onChangePassword(currentPassword, newPassword);
        toast.success(t('profileData.passwordChanged'));
        setPasswordVerified(false);
      } catch (e) {
        toast.error(
          getApiErrorMessage(e, t('profileData.passwordChangeError'))
        );
      }
    },
    [onChangePassword, setPasswordVerified, t]
  );

  if (!userData) return null;

  const personalContent = (
    <PersonalInfoSection
      userData={userData}
      form={form}
      setForm={setForm}
      editing={editingPersonal}
      onStartEdit={() => setEditingPersonal(true)}
      onStopEdit={() => setEditingPersonal(false)}
      onSave={handleSaveProfile}
      onDiscard={discardChanges}
      fields={personalFields}
      canEdit={canEditProfile}
    />
  );

  const contactContent = (
    <ContactInfoSection
      form={form}
      setForm={setForm}
      editing={editingContact}
      onStartEdit={() => setEditingContact(true)}
      onStopEdit={() => setEditingContact(false)}
      onSave={handleSaveProfile}
      onDiscard={discardChanges}
      fields={contactFields}
      canEdit={canEditProfile}
    />
  );

  const passwordContent = (
    <PasswordSection
      verified={passwordVerified}
      requireCurrentPassword={requireCurrentPassword}
      onVerifyPassword={handleVerifyPassword}
      onChangePassword={handleChangePassword}
    />
  );

  const rolesContent = (
    <RolesSection
      userData={userData}
      form={form}
      setForm={setForm}
      onSaveProfile={handleSaveProfile}
      showPrivilegesEditor={showPrivilegesEditor}
      onSaveRoleAndPrivileges={onSaveRoleAndPrivileges}
      roleOptions={roleOptions}
      categoryOptions={categoryOptions}
      isInternal={isInternal}
      privilegesUserId={privilegesUserId}
      roleId={roleId}
      roleFields={roleFields}
      userPrivileges={userPrivileges}
      hasCustomPrivileges={hasCustomPrivileges}
      onDirtyChange={handlePrivilegesDirtyChange}
      privilegesSaveRef={privilegesSaveRef}
      editing={editingRoles}
      onStartEdit={handleRoleStartEdit}
      onStopEdit={handleRoleStopEdit}
      onDiscard={handleRoleDiscard}
      onRolesFieldsDirtyChange={setRolesFieldsDirty}
      canEdit={canEditProfile}
    />
  );

  const tabs = [
    t('profileData.personalInfo'),
    t('profileData.contactInfo'),
    ...(showPasswordTab ? [t('profileData.password')] : []),
    ...(showRolesTab ? [t('profileData.rolesAndPrivileges')] : []),
  ];

  const tabContents = [
    personalContent,
    contactContent,
    ...(showPasswordTab ? [passwordContent] : []),
    ...(showRolesTab ? [rolesContent] : []),
  ];

  const sections = [
    {
      title: tabs[activeTab],
      content: (
        <>
          {tabContents.map((content, idx) =>
            mountedTabs.has(idx) ? (
              <div
                key={idx}
                style={{ display: activeTab === idx ? 'block' : 'none' }}
                aria-hidden={activeTab !== idx}
              >
                {content}
              </div>
            ) : null
          )}
        </>
      ),
    },
  ];

  return (
    <>
      <ProfileCommon
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        sections={sections}
      />
    </>
  );
};

export default SharedProfile;
